/* ════ LEAVE RECORDS ═══════════════════════════════════════ */
// A per-person, per-year record in the style of Vanessa's spreadsheet, built
// automatically from approved leave and bank holidays, plus absence records
// she adds by hand (sickness, medical, time off in lieu, other absence, leave
// adjustments). HR (approver + viewers) sees full records; everyone else sees
// only their own leave, bank holidays, time in lieu and balance. Access is
// enforced by the database (migration 0027); notes are encrypted (hrcrypto.js).
import { state } from './state.js';
import { $ } from './dom.js';
import { MO } from './config.js';
import { sb } from './supabase/client.js';
import { setSyncDot } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { pdate, fdate, addD, isWE } from './utils/date.js';
import { isBankHoliday, bankHolidayName } from './bankholidays.js';
import { hrConfigured, hrUnlocked, hrSetup, hrReset, hrUnlock, hrLock, hrRestore, encryptNote, decryptNote, currentKey, lockedNoteCount, oldKeyNoteCount } from './hrcrypto.js';
import {
  isApprover, isHR, people, avatar, nameFor, leaveDays, leaveSummary, fmtDays, fmtRange, allowanceRow, proRata, renderLeaveUI,
  bhDeducted, bhFrom,
} from './leave.js';

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const lc = s => String(s || '').toLowerCase();
const me = () => lc(state.currentUser?.email);
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dshort = iso => { const d = pdate(iso); return `${DOW[d.getDay()]} ${d.getDate()} ${MO[d.getMonth()]}`; };

// Medical appointments and time in lieu are usually hours; the rest days.
const DEFAULT_UNIT = { sick: 'days', medical: 'hours', in_lieu: 'hours', other: 'days', adjust: 'days', annual: 'days' };
// Types that are always in days (they move the leave balance).
const DAYS_ONLY = new Set(['adjust', 'annual']);
const isHours = a => a && a.hours != null && Number(a.hours) > 0;
export const fmtHours = h => `${Math.round(Number(h) * 4) / 4}h`;
export const KINDS = {
  sick: { label: 'Sickness', col: 'sick' },
  medical: { label: 'Medical appointment', col: 'med' },
  in_lieu: { label: 'Time off in lieu', col: 'lieu' },
  other: { label: 'Other absence', col: 'other' },
  adjust: { label: 'Leave adjustment', col: 'al' },
  annual: { label: 'Annual leave', col: 'al' },
};
const notes = new Map();   // decrypted notes, by record id (this tab only)

/* ── Build a record ─────────────────────────────────────── */
// Rows for one person and year, oldest first, with a running balance.
export function buildRecord(email, year, full) {
  const y = String(year), today = fdate(new Date());
  const al = allowanceRow(email, year), s = leaveSummary(email, Number(year));
  const rows = [];
  // Approved annual leave: one line per working day, like the spreadsheet.
  state.leaveRequests.filter(r => lc(r.user_email) === lc(email) && r.status === 'approved').forEach(r => {
    for (let d = pdate(r.start), e = pdate(r.end); d <= e; d = addD(d, 1)) {
      const iso = fdate(d);
      if (iso.slice(0, 4) !== y || isWE(d) || isBankHoliday(iso)) continue;
      const v = leaveDays(r.start, r.end, r.half, x => x === iso);
      if (v) rows.push({ date: iso, desc: v < 1 ? 'Annual leave (half day)' : 'Annual leave', col: 'al', days: v, future: iso >= today });
    }
  });
  // Bank holidays from the start date: deducted like a day of leave, or listed
  // for reference only, depending on the approver's setting.
  const ded = bhDeducted() && s.allowance != null;
  for (let d = pdate(bhFrom(al, year)), e = pdate(`${y}-12-31`); d <= e; d = addD(d, 1)) {
    const iso = fdate(d);
    if (!isWE(d) && isBankHoliday(iso)) rows.push({ date: iso, desc: bankHolidayName(iso) || 'Bank holiday', col: ded ? 'al' : 'bh', bh: true, days: ded ? 1 : 0, future: iso >= today });
  }
  // Absence records (HR sees all kinds; the database only gives others their in-lieu/adjustments).
  (state.absences || []).filter(a => lc(a.user_email) === lc(email) && a.start.slice(0, 4) === y)
    .filter(a => full || a.kind === 'in_lieu' || a.kind === 'adjust' || a.kind === 'annual')
    .forEach(a => rows.push({
      date: a.start, end: a.end, desc: KINDS[a.kind]?.label || a.kind, col: KINDS[a.kind]?.col || 'other',
      days: Number(a.days), hours: isHours(a) ? Number(a.hours) : 0, adjust: a.kind === 'adjust', rec: a, future: a.start >= today,
    }));
  rows.sort((a, b) => a.date.localeCompare(b.date) || (a.bh ? -1 : 1));
  // Running balance: annual leave comes off; adjustments add or remove.
  let bal = s.allowance;
  rows.forEach(r => {
    if (bal == null) return;
    if (r.col === 'al' && !r.adjust) bal -= r.days;
    if (r.adjust) bal += r.days;
    if (r.col === 'al') r.bal = bal;
  });
  const sum = c => rows.filter(r => r.col === c && !r.adjust).reduce((n, r) => n + r.days, 0);
  const hsum = c => rows.filter(r => r.col === c).reduce((n, r) => n + (r.hours || 0), 0);
  return {
    email, year: Number(year), al, summary: s, rows,
    totals: { med: sum('med'), sick: sum('sick'), lieu: sum('lieu'), other: sum('other'), al: sum('al'), bal },
    hours: { med: hsum('med'), sick: hsum('sick'), lieu: hsum('lieu'), other: hsum('other'), al: 0 },
    pr: al?.contract_days != null ? proRata(al.contract_days, al.start_date, Number(year)) : null,
  };
}

/* ── Render a record as a table ─────────────────────────── */
function recordTable(rec, full, editable) {
  const cols = full ? ['med', 'sick', 'lieu', 'other', 'al'] : ['lieu', 'al'];
  const head = { med: 'Med', sick: 'Sick', lieu: 'In lieu', other: 'Other', al: 'AL' };
  const n = v => (v ? fmtDays(v) : '');
  const { al, pr, summary: s } = rec;
  const entitlement = s.allowance == null
    ? `<tr class="rc-info"><td colspan="${cols.length + 3}">No ${rec.year} allowance set yet.</td></tr>`
    : `<tr class="rc-info"><td>Contract</td><td>Annual leave, 1 Jan – 31 Dec${al?.contract_days == null ? ' (set directly)' : ''}</td>${cols.map(c => `<td class="rt">${c === 'al' ? (pr?.raw != null ? fmtDays(al.contract_days) : `<b class="rc-ent">${fmtDays(s.allowance)}</b>`) : ''}</td>`).join('')}<td></td></tr>
       ${pr?.raw != null ? `<tr class="rc-info"><td></td><td>Pro-rata from ${dshort(al.start_date)} ${rec.year}: ${fmtDays(al.contract_days)} ÷ ${new Date(rec.year, 1, 29).getMonth() === 1 ? 366 : 365} × ${pr.rem} = ${(Math.round(pr.raw * 10) / 10).toFixed(1)}</td>${cols.map(c => `<td class="rt">${c === 'al' ? `<b class="rc-ent">${fmtDays(pr.days)}</b>` : ''}</td>`).join('')}<td></td></tr>` : ''}
       ${s.adjust ? `<tr class="rc-info"><td></td><td>Includes adjustments of ${s.adjust > 0 ? '+' : ''}${fmtDays(s.adjust)} (listed below)</td>${cols.map(() => '<td></td>').join('')}<td></td></tr>` : ''}`;
  const body = rec.rows.map(r => {
    const oldKey = r.rec?.note_enc && r.rec.key_id !== currentKey()?.id;
    const note = r.rec ? (notes.has(r.rec.id) ? `<span class="rc-note">${esc(notes.get(r.rec.id))}</span>`
      : (r.rec.note_enc && full ? `<span class="rc-note locked" title="${oldKey ? 'Saved with an older passphrase: kept, and readable again if that passphrase is entered' : 'Unlock notes to read'}">🔒 note${oldKey ? ' (older passphrase)' : ''}</span>` : '')) : '';
    const range = r.end && r.end !== r.date ? `${dshort(r.date)} – ${dshort(r.end)}` : dshort(r.date);
    const val = c => (r.col !== c ? '' : r.adjust ? `${r.days > 0 ? '+' : ''}${fmtDays(r.days)}` : r.hours ? fmtHours(r.hours) : n(r.days));
    return `<tr class="rc-${r.bh ? 'bh' : r.col}${r.future ? ' future' : ''}${editable && r.rec ? ' rc-edit' : ''}"${editable && r.rec ? ` data-abs="${r.rec.id}" title="Click to edit"` : ''}>
      <td class="rc-date">${range}</td><td>${esc(r.desc)}${r.bh && !r.days ? ' <span class="rc-nd">not deducted</span>' : ''}${r.future ? ' <span class="rc-nd">booked</span>' : ''} ${note}</td>
      ${cols.map(c => `<td class="rt">${val(c)}</td>`).join('')}<td class="rt rc-bal">${r.bal != null ? fmtDays(r.bal) : ''}</td></tr>`;
  }).join('');
  const t = rec.totals;
  return `<div class="rc-wrap"><table class="rc-table">
    <thead><tr><th>Date</th><th></th>${cols.map(c => `<th class="rt">${head[c]}</th>`).join('')}<th class="rt">Balance</th></tr></thead>
    <tbody>${entitlement}${body || `<tr><td colspan="${cols.length + 3}" class="lc-muted">Nothing recorded for ${rec.year} yet.</td></tr>`}</tbody>
    <tfoot><tr><td colspan="2">Totals ${rec.year}</td>${cols.map(c => `<td class="rt">${[n(t[c]), rec.hours[c] ? fmtHours(rec.hours[c]) : ''].filter(Boolean).join(' + ')}</td>`).join('')}<td class="rt"><b>${t.bal != null ? fmtDays(t.bal) : '–'}</b></td></tr></tfoot>
  </table></div>`;
}

async function decryptAll(list) {
  await Promise.all(list.filter(a => a.note_enc && !notes.has(a.id)).map(async a => { const t = await decryptNote(a); if (t != null) notes.set(a.id, t); }));
}

/* ── HR: Records tab ────────────────────────────────────── */
let resetOpen = false;
function lockBar() {
  if (resetOpen && isApprover()) return resetPanel();
  if (!hrConfigured()) {
    return isApprover()
      ? `<div class="rc-lock setup"><b>🔐 Set a notes passphrase</b><span>Notes on absence records are encrypted with it, so nobody without it can read them, even with access to the database. Share it with Will in person.</span>
          <span><b>Type it yourself and save a copy in your password manager first</b>; don't accept a password your browser suggests.</span>
          <div class="rc-lock-row"><input type="password" id="rc-pass1" placeholder="Passphrase (12+ characters)" ${NO_AUTOFILL}><input type="password" id="rc-pass2" placeholder="Type it again" ${NO_AUTOFILL}><button class="lv-btn rc-eye" type="button" data-eye="rc-pass1,rc-pass2" aria-label="Show passphrase" title="Show / hide">👁</button><button class="lv-btn ok" id="rc-setup">Set passphrase</button></div></div>`
      : '<div class="rc-lock"><span>🔐 Notes are locked until Vanessa sets up the notes passphrase.</span></div>';
  }
  const resetLink = isApprover() ? '<button class="lnk-btn" id="rc-reset-open" type="button">Forgotten it? Reset passphrase…</button>' : '';
  if (hrUnlocked()) {
    const older = lockedNoteCount();
    return `<div class="rc-lock open"><span>🔓 Notes unlocked in this tab.</span><button class="lv-btn" id="rc-lock">Lock</button>
      ${older ? `<div class="rc-lock-row rc-older"><span>🔒 ${older} ${older === 1 ? 'note was' : 'notes were'} saved with an older passphrase. If you find it, enter it here:</span>
        <input type="password" id="rc-pass" placeholder="Older passphrase" ${NO_AUTOFILL}><button class="lv-btn rc-eye" type="button" data-eye="rc-pass" aria-label="Show passphrase" title="Show / hide">👁</button><button class="lv-btn" id="rc-unlock">Unlock older notes</button></div>` : ''}</div>`;
  }
  return `<div class="rc-lock"><span>🔒 Notes are encrypted. Enter the passphrase to read or add them.</span>
    <div class="rc-lock-row"><input type="password" id="rc-pass" placeholder="Notes passphrase" ${NO_AUTOFILL}><button class="lv-btn rc-eye" type="button" data-eye="rc-pass" aria-label="Show passphrase" title="Show / hide">👁</button><button class="lv-btn ok" id="rc-unlock">Unlock</button></div>
    ${resetLink}</div>`;
}

// Approver: reset a forgotten passphrase, with a plain account of what happens.
function resetPanel() {
  // Notes a reset would lock: those saved with the current passphrase.
  const cur = currentKey()?.id, total = (state.absences || []).length;
  const n = (state.absences || []).filter(a => a.note_enc && (a.key_id === cur || !a.key_id)).length;
  return `<div class="rc-lock reset"><b>Reset the notes passphrase</b>
    <ul class="rc-reset-list">
      <li><b>Not affected:</b> all ${total} absence records (dates, types, days and hours), annual leave, allowances and balances.</li>
      <li><b>Locked:</b> ${n ? `the ${n} ${n === 1 ? 'note' : 'notes'} saved with the current passphrase. ${n === 1 ? 'It is' : 'They are'} kept, not deleted, and can be read again if the current passphrase is ever entered.` : 'no notes (none have been saved yet).'}</li>
      <li><b>From now on:</b> new notes use the new passphrase. Will needs the new one too.</li>
    </ul>
    <span><b>Type the new passphrase yourself and save it in your password manager first.</b></span>
    <div class="rc-lock-row"><input type="password" id="rc-new1" placeholder="New passphrase (12+ characters)" ${NO_AUTOFILL}><input type="password" id="rc-new2" placeholder="Type it again" ${NO_AUTOFILL}><button class="lv-btn rc-eye" type="button" data-eye="rc-new1,rc-new2" aria-label="Show passphrase" title="Show / hide">👁</button></div>
    <div class="rc-lock-row"><label class="rc-confirm">Type <b>RESET</b> to confirm <input id="rc-reset-word" ${NO_AUTOFILL} autocomplete="off" size="8"></label>
      <button class="lv-btn no" id="rc-reset-go">Reset passphrase</button><button class="lv-btn" id="rc-reset-cancel">Cancel</button></div></div>`;
}
// Keep browsers and password managers from auto-filling a login password into
// (or suggesting a generated password for) the notes passphrase boxes.
const NO_AUTOFILL = 'autocomplete="one-time-code" autocapitalize="off" spellcheck="false" data-lpignore="true" data-1p-ignore data-bwignore';

export async function renderRecordsTab(body) {
  if (!isHR()) { body.innerHTML = '<div class="list-empty">Only HR can see records.</div>'; return; }
  await hrRestore();
  const ppl = people();
  if (!state.recordEmail || !ppl.some(e => lc(e) === lc(state.recordEmail))) state.recordEmail = ppl[0] || null;
  const email = state.recordEmail;
  if (!email) { body.innerHTML = '<div class="list-empty">No people yet.</div>'; return; }
  await decryptAll(state.absences);
  const rec = buildRecord(email, state.leaveYear, true);
  body.innerHTML = `${lockBar()}
    <div class="rc-head">
      <label class="rc-person">${avatar(email)}<select id="rc-who" aria-label="Person">${ppl.map(e => `<option value="${esc(e)}"${lc(e) === lc(email) ? ' selected' : ''}>${esc(nameFor(e))}</option>`).join('')}</select></label>
      <span class="rc-years" role="group" aria-label="Year">${recordYears().map(y => `<button class="rc-yr${y === rec.year ? ' on' : ''}" data-ry="${y}" aria-pressed="${y === rec.year}">${y}</button>`).join('')}</span>
      <span class="rc-spacer"></span>
      ${isApprover() ? '<button class="lv-btn ok" id="rc-add">＋ Add absence</button>' : ''}
      <button class="lv-btn" id="rc-xlsx">⬇ This person</button>
      <button class="lv-btn" id="rc-xlsx-all">⬇ Everyone (.xlsx)</button>
    </div>
    ${recordTable(rec, true, isApprover())}
    <div class="rc-foot">Annual leave comes from approved requests. ${bhDeducted() ? 'Bank holidays come off the allowance.' : 'Bank holidays are listed but not deducted.'} Sickness, medical and other absence are visible to Vanessa and Will only; the person sees their own leave, time in lieu and balance.</div>`;
}

// This year and next (plus whatever year is picked in the dropdown).
function recordYears() {
  const y = new Date().getFullYear(), ys = new Set([y, y + 1, state.leaveYear]);
  return [...ys].sort();
}

/* ── Employee: my own record (in My leave) ──────────────── */
export function myRecordHTML(year) {
  return recordTable(buildRecord(me(), year, false), false, false);
}

/* ── Add / edit absence (approver) ──────────────────────── */
let editing = null;
function openAbsence(rec = null) {
  editing = rec;
  const who = rec?.user_email || state.recordEmail;
  $('ab-title').textContent = rec ? 'Edit absence' : 'Add absence';
  $('ab-who').innerHTML = people().map(e => `<option value="${esc(e)}"${lc(e) === lc(who) ? ' selected' : ''}>${esc(nameFor(e))}</option>`).join('');
  $('ab-kind').value = rec?.kind || 'sick';
  const t = fdate(new Date());
  $('ab-start').value = rec?.start || (String(state.leaveYear) === t.slice(0, 4) ? t : `${state.leaveYear}-01-01`);
  $('ab-end').value = rec?.end || $('ab-start').value;
  $('ab-start').dataset.prev = $('ab-start').value;
  $('ab-halfday').checked = rec ? rec.half === 'pm' : false;
  $('ab-unit').value = rec ? (isHours(rec) ? 'hours' : 'days') : DEFAULT_UNIT.sick;
  $('ab-days').value = rec ? (isHours(rec) ? String(Number(rec.hours)) : fmtDays(Number(rec.days))) : '';
  $('ab-note').value = rec ? (notes.get(rec.id) || '') : '';
  $('ab-del').style.display = rec ? '' : 'none';
  $('ab-err').textContent = '';
  absenceKindChanged(!rec);
  $('abs-modal').classList.add('open');
}
function absenceKindChanged(recalc = true) {
  const adj = $('ab-kind').value === 'adjust';
  if (recalc) $('ab-unit').value = DEFAULT_UNIT[$('ab-kind').value] || 'days';
  if (DAYS_ONLY.has($('ab-kind').value)) $('ab-unit').value = 'days';
  unitChanged(false);
  $('ab-end-wrap').style.display = adj ? 'none' : '';
  const locked = hrConfigured() && !hrUnlocked(), noKey = !hrConfigured();
  // A note that can't be read in this tab (e.g. saved with an older passphrase)
  // is kept exactly as it is: the box is disabled so saving can't wipe it.
  const unreadable = !!(editing?.note_enc && !notes.has(editing.id));
  $('ab-note').disabled = locked || noKey || unreadable;
  $('ab-note-hint').textContent = unreadable ? 'This note is locked (older or current passphrase not entered). It is kept as it is.'
    : noKey ? 'Set up the notes passphrase first to add private notes.' : locked ? 'Unlock notes (top of Records) to add or read the note.' : 'Encrypted: only Vanessa and Will can read this.';
  if (recalc) recalcAbsenceDays();
}
// Days: worked out from the dates (half day allowed). Hours: typed in.
function unitChanged(recalc = true) {
  const adj = $('ab-kind').value === 'adjust', hrs = $('ab-unit').value === 'hours';
  $('ab-unit').style.display = DAYS_ONLY.has($('ab-kind').value) ? 'none' : '';
  $('ab-days-lbl').textContent = adj ? 'Days (+ adds to their balance, − takes off)' : hrs ? 'Time off (hours)' : 'Time off (working days)';
  $('ab-days').step = hrs ? '0.25' : '0.5';
  $('ab-days').placeholder = hrs ? 'e.g. 1.5' : '';
  $('ab-half-wrap').style.display = adj || hrs ? 'none' : '';
  if (recalc) { if (hrs) { $('ab-days').value = ''; $('ab-days').focus(); } else recalcAbsenceDays(); }
}
function recalcAbsenceDays() {
  if ($('ab-kind').value === 'adjust' || $('ab-unit').value === 'hours') return;
  const s = $('ab-start').value, e = $('ab-end').value || s;
  if (!s || e < s) return;
  const half = $('ab-halfday').checked && s === e ? 'pm' : 'none';
  $('ab-days').value = fmtDays(leaveDays(s, e, half));
}
async function saveAbsence() {
  const kind = $('ab-kind').value, adj = kind === 'adjust';
  const start = $('ab-start').value, end = adj ? start : ($('ab-end').value || start);
  const hrs = !adj && $('ab-unit').value === 'hours';
  const raw = $('ab-days').value.trim(), amount = Number(raw);
  if (!start) { $('ab-err').textContent = 'Pick a date.'; return; }
  if (end < start) { $('ab-err').textContent = 'The end date is before the start date.'; return; }
  if (raw === '' || !Number.isFinite(amount) || (adj ? amount === 0 : amount <= 0)) {
    $('ab-err').textContent = adj ? 'Enter the days to add (e.g. 1) or remove (e.g. -1).' : hrs ? 'Enter the hours (e.g. 1.5).' : 'Enter the number of working days.';
    return;
  }
  const days = hrs ? 0 : amount, hours = hrs ? Math.round(amount * 4) / 4 : null;
  let sealed = { note_enc: editing?.note_enc || null, note_iv: editing?.note_iv || null, key_id: editing?.key_id || null };
  const text = $('ab-note').value.trim();
  if (!$('ab-note').disabled) {
    try { sealed = await encryptNote(text); } catch (e) { $('ab-err').textContent = e.message; return; }
  }
  const row = { user_email: $('ab-who').value, kind, start, end, half: $('ab-halfday').checked && start === end && !adj && !hrs ? 'pm' : 'none',
    days: Math.round(days * 2) / 2, hours, ...sealed, updated_at: new Date().toISOString() };
  setSyncDot('busy');
  const q = editing ? sb.from('absence_records').update(row).eq('id', editing.id).select().single()
    : sb.from('absence_records').insert({ ...row, created_by: state.currentUser.email }).select().single();
  const { data, error } = await q;
  if (error) { setSyncDot('err'); $('ab-err').textContent = /hours/.test(error.message) ? 'Run migration 0029 first.' : /absence_records/.test(error.message) ? 'Run migration 0027 first.' : 'Could not save: ' + error.message; return; }
  const i = state.absences.findIndex(a => a.id === data.id);
  if (i >= 0) state.absences[i] = data; else state.absences.push(data);
  if (text && !$('ab-note').disabled) notes.set(data.id, text); else if (!$('ab-note').disabled) notes.delete(data.id);
  state.recordEmail = data.user_email;
  setSyncDot('ok'); $('abs-modal').classList.remove('open');
  showToast(`Saved · ${KINDS[kind].label} for ${nameFor(data.user_email)}`);
  renderLeaveUI();
}
async function deleteAbsence() {
  if (!editing || !confirm(`Delete this ${KINDS[editing.kind]?.label.toLowerCase()} record for ${nameFor(editing.user_email)}?`)) return;
  setSyncDot('busy');
  const { error } = await sb.from('absence_records').delete().eq('id', editing.id);
  if (error) { setSyncDot('err'); $('ab-err').textContent = 'Could not delete: ' + error.message; return; }
  state.absences = state.absences.filter(a => a.id !== editing.id);
  setSyncDot('ok'); $('abs-modal').classList.remove('open'); showToast('Record deleted'); renderLeaveUI();
}

/* ── Download as Excel ──────────────────────────────────── */
async function getXLSX() { const m = await import('xlsx'); return m.utils ? m : m.default; }
const num = v => (v ? Number(fmtDays(v)) : '');
const both = (d, h) => [num(d), h ? fmtHours(h) : ''].filter(x => x !== '').join(' + ');

// One person's record as rows, in the layout of the leave spreadsheet.
function recordSheet(XLSX, rec) {
  const aoa = [[nameFor(rec.email)], [], ['Date', 'Description', 'Med', 'Sick', 'In lieu', 'Other', 'AL', 'Balance'], [String(rec.year)]];
  if (rec.summary.allowance != null) {
    aoa.push(['Contract', 'Annual leave calculated 1 Jan – 31 Dec', '', '', '', '', Number(rec.al?.contract_days ?? rec.summary.allowance), '']);
    if (rec.pr?.raw != null) aoa.push(['', `Pro-rata from ${rec.al.start_date}`, '', '', '', '', rec.pr.days, '']);
  } else aoa.push(['', `No ${rec.year} allowance set`]);
  rec.rows.forEach(r => {
    const c = k => (r.col === k ? (r.adjust ? r.days : r.hours ? fmtHours(r.hours) : num(r.days)) : '');
    aoa.push([r.end && r.end !== r.date ? `${r.date} – ${r.end}` : r.date,
      r.desc + (r.bh && !r.days ? ' (not deducted)' : '') + (r.rec && notes.has(r.rec.id) ? ` · ${notes.get(r.rec.id)}` : ''),
      c('med'), c('sick'), c('lieu'), c('other'), c('al'), r.bal != null ? Number(fmtDays(r.bal)) : '']);
  });
  const t = rec.totals, h = rec.hours;
  aoa.push([], ['Totals', '', both(t.med, h.med), both(t.sick, h.sick), both(t.lieu, h.lieu), both(t.other, h.other), num(t.al), t.bal != null ? Number(fmtDays(t.bal)) : '']);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 22 }, { wch: 44 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 7 }, { wch: 9 }];
  return ws;
}
// Sheet names: max 31 chars, no []:*?/\ and unique.
function sheetName(name, used) {
  let base = String(name).replace(/[[\]:*?/\\]/g, ' ').slice(0, 28) || 'Person', n = base, i = 2;
  while (used.has(n.toLowerCase())) n = `${base} ${i++}`;
  used.add(n.toLowerCase()); return n;
}

async function downloadXlsx() {
  const XLSX = await getXLSX();
  const rec = buildRecord(state.recordEmail, state.leaveYear, true);
  const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, recordSheet(XLSX, rec), String(rec.year));
  XLSX.writeFile(wb, `Leave record - ${nameFor(rec.email)} - ${rec.year}.xlsx`);
}

// Everyone at once: a summary sheet, then one sheet per person.
async function downloadAllXlsx() {
  const XLSX = await getXLSX(), y = state.leaveYear, used = new Set(['summary']);
  const recs = people().map(e => buildRecord(e, y, true));
  const summary = [[`Leave summary ${y}`], [],
    ['Person', 'Contract', 'Entitlement', 'Adjustments', 'Leave taken', 'Leave booked', 'Bank holidays', 'Left', 'Pending', 'Med', 'Sick', 'In lieu', 'Other']];
  recs.forEach(r => {
    const s = r.summary, t = r.totals, h = r.hours;
    summary.push([nameFor(r.email), r.al?.contract_days != null ? Number(r.al.contract_days) : '', s.allowance ?? 'not set',
      num(s.adjust), num(s.taken), num(s.booked), s.bh || '', s.left != null ? Number(fmtDays(s.left)) : '', num(s.pending),
      both(t.med, h.med), both(t.sick, h.sick), both(t.lieu, h.lieu), both(t.other, h.other)]);
  });
  const ws = XLSX.utils.aoa_to_sheet(summary);
  ws['!cols'] = [{ wch: 20 }, ...Array(12).fill({ wch: 11 })];
  const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Summary');
  recs.forEach(r => XLSX.utils.book_append_sheet(wb, recordSheet(XLSX, r), sheetName(nameFor(r.email), used)));
  XLSX.writeFile(wb, `Leave records - everyone - ${y}.xlsx`);
  showToast(`⬇ ${recs.length} records downloaded${hrUnlocked() ? '' : ' (notes left out: unlock to include them)'}`);
}

/* ── Wiring ─────────────────────────────────────────────── */
export function initRecords() {
  const body = $('lv-body'); if (!body) return;
  body.addEventListener('change', e => { if (e.target.id === 'rc-who') { state.recordEmail = e.target.value; renderLeaveUI(); } });
  body.addEventListener('click', async e => {
    const t = e.target;
    if (t.closest('#rc-add')) return openAbsence();
    const yb = t.closest('[data-ry]');
    if (yb) { state.leaveYear = Number(yb.dataset.ry); const ys = $('lv-year'); if (ys) ys.value = String(state.leaveYear); return renderLeaveUI(); }
    if (t.closest('#rc-xlsx-all')) return downloadAllXlsx();
    if (t.closest('#rc-xlsx')) return downloadXlsx();
    if (t.closest('#rc-lock')) { hrLock(); notes.clear(); return renderLeaveUI(); }
    const eye = t.closest('[data-eye]');
    if (eye) {
      const ids = eye.dataset.eye.split(','), show = $(ids[0])?.type === 'password';
      ids.forEach(id => { const i = $(id); if (i) i.type = show ? 'text' : 'password'; });
      eye.setAttribute('aria-label', show ? 'Hide passphrase' : 'Show passphrase');
      return;
    }
    if (t.closest('#rc-unlock')) {
      const res = await hrUnlock($('rc-pass').value);
      if (!res.current && !res.older) { showToast('That passphrase isn\'t right'); $('rc-pass').select(); return; }
      showToast(res.current ? `🔓 Notes unlocked${res.older ? ' (and older notes too)' : ''}` : '🔓 Older notes unlocked' + (hrUnlocked() ? '' : '. Enter the current passphrase to add new notes.'));
      return renderLeaveUI();
    }
    if (t.closest('#rc-reset-open')) { resetOpen = true; return renderLeaveUI(); }
    if (t.closest('#rc-reset-cancel')) { resetOpen = false; return renderLeaveUI(); }
    if (t.closest('#rc-reset-go')) {
      const a = $('rc-new1').value.trim(), b = $('rc-new2').value.trim();
      if (a.length < 12) { showToast('Use at least 12 characters'); return; }
      if (a !== b) { showToast('The two passphrases don\'t match'); return; }
      if ($('rc-reset-word').value.trim().toUpperCase() !== 'RESET') { showToast('Type RESET to confirm'); $('rc-reset-word').focus(); return; }
      try { await hrReset(a); } catch (err) { showToast('Could not reset: ' + (err.message || err)); return; }
      resetOpen = false;
      showToast('🔐 New passphrase set. Share it with Will in person.'); return renderLeaveUI();
    }
    if (t.closest('#rc-setup')) {
      const a = $('rc-pass1').value, b = $('rc-pass2').value;
      if (a.length < 12) { showToast('Use at least 12 characters'); return; }
      if (a !== b) { showToast('The two passphrases don\'t match'); return; }
      try { await hrSetup(a); } catch (err) { showToast('Could not save: ' + err.message); return; }
      showToast('🔐 Passphrase set. Share it with Will in person.'); return renderLeaveUI();
    }
    const row = t.closest('[data-abs]');
    if (row && isApprover()) { const r = state.absences.find(a => a.id === row.dataset.abs); if (r) openAbsence(r); }
  });
  body.addEventListener('keydown', e => { if (e.key === 'Enter' && e.target.id === 'rc-pass') $('rc-unlock')?.click(); });
  $('ab-kind').addEventListener('change', () => absenceKindChanged(true));
  // Moving the start date carries a single-day entry with it (and never leaves the end before the start).
  $('ab-start').addEventListener('focus', () => { $('ab-start').dataset.prev = $('ab-start').value; });
  $('ab-start').addEventListener('change', () => {
    const s = $('ab-start').value, e = $('ab-end').value, prev = $('ab-start').dataset.prev;
    if (!e || e < s || e === prev) $('ab-end').value = s;
    $('ab-start').dataset.prev = s;
    recalcAbsenceDays();
  });
  $('ab-end').addEventListener('change', recalcAbsenceDays);
  $('ab-halfday').addEventListener('change', recalcAbsenceDays);
  $('ab-unit').addEventListener('change', () => unitChanged(true));
  $('ab-save').addEventListener('click', saveAbsence);
  $('ab-del').addEventListener('click', deleteAbsence);
  ['ab-cancel', 'ab-x'].forEach(id => $(id).addEventListener('click', () => $('abs-modal').classList.remove('open')));
}
